/** AP invoices and credit notes. Keys are flat, dot-namespaced and stable; placeholders use {name}. */
export const enBoApInvoices = {
  // ---- Statuses and reasons
  'bo.ap.status.draft': 'Draft',
  'bo.ap.status.under_review': 'Under review',
  'bo.ap.status.approved': 'Approved',
  'bo.ap.status.posted': 'Posted',
  'bo.ap.status.partially_paid': 'Partially paid',
  'bo.ap.status.paid': 'Paid',
  'bo.ap.status.disputed': 'Disputed',
  'bo.ap.status.voided': 'Voided',
  'bo.ap.reason.line_not_on_po': 'Not on the PO',
  'bo.ap.reason.qty_exceeds_ordered': 'Quantity above ordered',
  'bo.ap.reason.qty_exceeds_received': 'Quantity above received',
  'bo.ap.reason.price_variance': 'Price outside tolerance',
  'bo.ap.reason.amount_variance': 'Header amount differs from lines',

  // ---- Invoice list
  'bo.ap.inv.title': 'Supplier invoices',
  'bo.ap.inv.subtitle':
    'Invoices received from suppliers in {branch}: matching, approval and payable.',
  'bo.ap.inv.noBranch': 'Supplier invoices belong to a branch. Create a branch first.',
  'bo.ap.inv.add': 'Record invoice',
  'bo.ap.inv.readOnly': 'Read only: recording and changing invoices needs the AP staff role.',
  'bo.ap.inv.noPermission': 'Your role cannot see supplier invoices in this branch.',
  'bo.ap.inv.listTitle': 'Invoices',
  'bo.ap.inv.listDesc':
    'Newest first. Outstanding is the open payable after verified payments and posted credit notes.',
  'bo.ap.inv.filter.status': 'Filter by status',
  'bo.ap.inv.filter.supplier': 'Supplier',
  'bo.ap.inv.filter.allSuppliers': 'All suppliers',
  'bo.ap.inv.filter.openOnly': 'Open payables only',
  'bo.ap.inv.search': 'Number, supplier or supplier invoice no.',
  'bo.ap.inv.empty': 'No supplier invoices yet. Record the first one with “Record invoice”.',
  'bo.ap.inv.emptyReadOnly': 'No supplier invoices yet. They appear once AP staff record them.',
  'bo.ap.inv.emptyFiltered': 'No invoices match these filters.',
  'bo.ap.inv.col.number': 'Number',
  'bo.ap.inv.col.supplier': 'Supplier',
  'bo.ap.inv.col.supplierNumber': 'Invoice no. {number}',
  'bo.ap.inv.col.match': 'Match',
  'bo.ap.inv.col.status': 'Status',
  'bo.ap.inv.col.pending': 'Pending',
  'bo.ap.inv.col.line': 'Line',
  'bo.ap.inv.col.qty': 'Quantity',
  'bo.ap.inv.col.price': 'Unit price',
  'bo.ap.inv.col.discount': 'Discount',
  'bo.ap.inv.col.taxRate': 'Tax',
  'bo.ap.inv.col.net': 'Net',
  'bo.ap.inv.col.tax': 'Tax amount',
  'bo.ap.inv.overdue': 'Overdue {days} days',
  'bo.ap.inv.refreshing': 'Refreshing…',
  'bo.ap.inv.stale': 'Could not refresh — showing the last loaded data.',

  // ---- Shared field labels
  'bo.ap.inv.supplier': 'Supplier',
  'bo.ap.inv.pickSupplier': 'Choose a supplier',
  'bo.ap.inv.supplierNumber': 'Supplier invoice number',
  'bo.ap.inv.supplierNumberHint': 'As printed on the supplier’s invoice.',
  'bo.ap.inv.invoiceDate': 'Invoice date',
  'bo.ap.inv.dueDate': 'Due date',
  'bo.ap.inv.dueHint': 'Default: invoice date + {days} days (supplier payment terms).',
  'bo.ap.inv.dueHintNoSupplier': 'Default: invoice date + the supplier’s payment terms.',
  'bo.ap.inv.po': 'Purchase order',
  'bo.ap.inv.noPo': 'No purchase order',
  'bo.ap.inv.poHint': 'Lines are filled from the order and matched against it.',
  'bo.ap.inv.poFixed': 'The purchase order of a recorded invoice cannot change.',
  'bo.ap.inv.poOption': '{number} — {total}',
  'bo.ap.inv.poLoading': 'Loading the order lines…',
  'bo.ap.inv.poLoadFailed': 'The order lines could not be loaded. Choose the order again.',
  'bo.ap.inv.posLoadFailed':
    'Purchase orders could not be loaded; record without an order or retry later.',
  'bo.ap.inv.posEmpty': 'No approved purchase orders from this supplier.',
  'bo.ap.inv.budgetHint':
    'Approved lines of {period}; the net amount becomes an actual when posted.',
  'bo.ap.inv.notes': 'Notes',
  'bo.ap.inv.noSuppliers': 'No active suppliers yet. Suppliers are managed in Stock.',
  'bo.ap.inv.perUnit': 'per {unit}',
  'bo.ap.inv.net': 'Net',
  'bo.ap.inv.tax': 'Tax',
  'bo.ap.inv.total': 'Total',
  'bo.ap.inv.outstanding': 'Outstanding',
  'bo.ap.inv.pending': 'Pending allocations',
  'bo.ap.inv.openToAllocate': 'Open to pay or credit',
  'bo.ap.inv.notPostedYet': 'Not posted yet',

  // ---- Form
  'bo.ap.inv.newTitle': 'Record supplier invoice',
  'bo.ap.inv.newSubtitle':
    'Copy the paper invoice. It is saved as a draft; matching and approval come next.',
  'bo.ap.inv.noManage': 'Recording invoices needs the AP staff role (ap.manage).',
  'bo.ap.inv.editTitle': 'Edit {number}',
  'bo.ap.inv.editSubtitle': 'Saving replaces every line and clears the last match.',
  'bo.ap.inv.form.headTitle': 'Invoice',
  'bo.ap.inv.form.headDesc': 'Supplier, numbers and dates as on the paper invoice.',
  'bo.ap.inv.form.linesTitle': 'Lines',
  'bo.ap.inv.form.linesDesc':
    'Free lines (freight, services). Quantity with up to 3 decimals; prices in rupiah per unit.',
  'bo.ap.inv.form.linesDescPo':
    'Filled from the order (received quantity, or ordered before any receipt). Correct them to what the supplier invoiced.',
  'bo.ap.inv.form.lineLabel': 'Line {n}',
  'bo.ap.inv.form.removeLine': 'Remove line {n}',
  'bo.ap.inv.form.poLine': 'PO line: {item} · in {unit}',
  'bo.ap.inv.form.freeLine': 'Free line (not on the purchase order)',
  'bo.ap.inv.form.description': 'Description',
  'bo.ap.inv.form.qty': 'Quantity ({unit})',
  'bo.ap.inv.form.unitFree': 'units',
  'bo.ap.inv.form.price': 'Unit price ({currency})',
  'bo.ap.inv.form.discount': 'Discount',
  'bo.ap.inv.form.taxRate': 'Tax %',
  'bo.ap.inv.form.lineNet': 'Line net',
  'bo.ap.inv.form.lineTax': 'tax',
  'bo.ap.inv.form.addLine': 'Add free line',
  'bo.ap.inv.form.amountsTitle': 'Amounts as printed',
  'bo.ap.inv.form.amountsDesc':
    'Type the net and tax printed on the invoice; they are checked against the lines.',
  'bo.ap.inv.form.useLineTotals': 'Use line totals',
  'bo.ap.inv.form.headerNet': 'Net printed ({currency})',
  'bo.ap.inv.form.headerTax': 'Tax printed ({currency})',
  'bo.ap.inv.form.linesSum': 'Lines: {amount}',
  'bo.ap.inv.form.total': 'Invoice total',
  'bo.ap.inv.form.headerMatches': 'The printed amounts equal the sums of the lines.',
  'bo.ap.inv.form.headerDiff': 'Net printed vs lines:',
  'bo.ap.inv.form.taxDiff': 'tax:',
  'bo.ap.inv.form.headerOutside': 'outside the tolerance; matching will flag it.',
  'bo.ap.inv.form.draftNote': 'Saved as a draft. Nothing is payable until it is posted.',
  'bo.ap.inv.form.saveDraft': 'Save draft',
  'bo.ap.inv.err.supplier': 'Choose the supplier.',
  'bo.ap.inv.err.number': 'Enter the supplier invoice number (at most 60 characters).',
  'bo.ap.inv.err.date': 'Enter a valid date.',
  'bo.ap.inv.err.dueBefore': 'The due date is before the invoice date.',
  'bo.ap.inv.err.amount': 'Enter a whole amount in rupiah.',
  'bo.ap.inv.err.notes': 'Notes can have at most 500 characters.',
  'bo.ap.inv.err.noLines': 'Add at least one line.',
  'bo.ap.inv.err.tooManyLines': 'An invoice can have at most 200 lines.',
  'bo.ap.inv.err.desc': 'Describe the line (at most 200 characters).',
  'bo.ap.inv.err.qty': 'Enter a quantity above 0 with at most 3 decimals.',
  'bo.ap.inv.err.price': 'Enter the unit price in whole rupiah.',
  'bo.ap.inv.err.tax': 'Enter a tax rate from 0 to 100% (at most 2 decimals).',
  'bo.ap.inv.err.discount': 'The discount is larger than the line amount.',
  'bo.ap.inv.err.matchTolerance':
    'The match is outside tolerance and was not approved as an exception. Match again or dispute the invoice.',
  'bo.ap.inv.err.hasAllocations':
    'Payments or credit notes are allocated to this invoice. Reject or void them first.',
  'bo.ap.inv.dup.title': 'This supplier invoice number is already recorded',
  'bo.ap.inv.dup.body': 'The same supplier has a non-voided invoice with this number:',
  'bo.ap.inv.dup.open': 'open {number}',
  'bo.ap.inv.dup.field': 'Already recorded for this supplier.',
  'bo.ap.inv.dup.override': 'Record it anyway as a controlled exception',
  'bo.ap.inv.dup.overrideHint':
    'Only when the supplier really issued two invoices with one number. The reason is kept on the invoice and in the audit trail.',
  'bo.ap.inv.dup.reason': 'Reason for the exception',
  'bo.ap.inv.dup.reasonInvalid': 'Write a reason of 3 to 300 characters.',
  'bo.ap.inv.dup.noOverride':
    'Check the existing invoice. Only a finance manager (ap.approve) can record an exception.',
  'bo.ap.inv.dup.saveOverride': 'Save as exception',
  'bo.ap.inv.created': 'Invoice {number} saved as a draft.',
  'bo.ap.inv.saved': 'Invoice {number} saved.',

  // ---- Detail
  'bo.ap.inv.backToList': 'All invoices',
  'bo.ap.inv.detailTitle': 'Supplier invoice',
  'bo.ap.inv.detailSubtitle': '{supplier} · invoice no. {number}',
  'bo.ap.inv.readOnlyDetail': 'Read only: changing this invoice needs the AP staff role.',
  'bo.ap.inv.factsTitle': 'Details',
  'bo.ap.inv.factsDesc': 'What was recorded, by whom, and against which order and budget.',
  'bo.ap.inv.amountsTitle': 'Amounts',
  'bo.ap.inv.amountsDesc':
    'Outstanding counts verified payments and posted credit notes; pending ones still await a second person.',
  'bo.ap.inv.matchMode': 'Matching',
  'bo.ap.inv.mode.two_way': '2-way (PO)',
  'bo.ap.inv.mode.three_way': '3-way (PO + receipt)',
  'bo.ap.inv.mode.none': 'No PO — not matched',
  'bo.ap.inv.matchStatus.not_matched': 'Not matched',
  'bo.ap.inv.matchStatus.matched': 'Matched',
  'bo.ap.inv.matchStatus.exception': 'Exception',
  'bo.ap.inv.matchStatus.overridden': 'Exception approved',
  'bo.ap.inv.createdBy': 'Recorded by',
  'bo.ap.inv.postedBy': 'Posted by',
  'bo.ap.inv.reasonText': 'Reason: {reason}',
  'bo.ap.inv.voidedAt': 'Voided at {time}',
  'bo.ap.inv.disputedTitle': 'Disputed',
  'bo.ap.inv.disputedHelp':
    'Settle it with the supplier, then reopen it as a draft to correct and submit it again.',
  'bo.ap.inv.disputedHelpReadOnly': 'AP staff reopen it once it is settled with the supplier.',
  'bo.ap.inv.dupOverrideTitle': 'Recorded as an exception to the duplicate-number rule',
  'bo.ap.inv.exceptionReviewTitle': 'Matching exception',
  'bo.ap.inv.exceptionReviewBody':
    'The approval includes an extra “Match exception” step for a finance manager; approving it overrides the exception.',
  'bo.ap.inv.exceptionDraftTitle': 'Matching found exceptions',
  'bo.ap.inv.exceptionDraftBody':
    'Correct the lines or ask the supplier for a credit note. Submitting anyway adds a “Match exception” approval step.',
  'bo.ap.inv.exceptionStepNote':
    'The match is outside tolerance: the request has an extra “Match exception” step.',
  'bo.ap.inv.draftNote': 'Draft: match it against the order, then submit it for approval.',
  'bo.ap.inv.draftNoteReadOnly': 'Draft: AP staff submit it for approval.',
  'bo.ap.inv.approvedNote': 'Approved: posting records the payable and the budget actual.',
  'bo.ap.inv.linesTitle': 'Invoice lines',
  'bo.ap.inv.linesDesc': 'As recorded; net is after discount, tax per line.',
  'bo.ap.inv.linesEmpty': 'No lines.',
  'bo.ap.inv.linesSum': 'Sum of the lines',
  'bo.ap.inv.lineOnPo': 'On the purchase order',

  // ---- Match view
  'bo.ap.inv.match': 'Match',
  'bo.ap.inv.matching': 'Matching…',
  'bo.ap.inv.matchedOk': 'Matched within tolerance.',
  'bo.ap.inv.matchedException': 'Matched with exceptions — see the reasons per line.',
  'bo.ap.inv.match.title': 'Ordered · received · invoiced',
  'bo.ap.inv.match.descThree':
    '3-way: the invoiced quantity is checked against the ordered and the received quantity, the price against the PO.',
  'bo.ap.inv.match.descTwo':
    '2-way: the invoiced quantity and price are checked against the purchase order only.',
  'bo.ap.inv.match.descNone': 'Invoices without a purchase order are not matched.',
  'bo.ap.inv.match.tolerance':
    'Tolerance: price ±{price}%, quantity +{qty}%, header amount ±{amount}.',
  'bo.ap.inv.match.runAt': 'Last matched {time}.',
  'bo.ap.inv.match.noPo': 'No purchase order — nothing to match against.',
  'bo.ap.inv.match.notRun':
    'Not matched yet. “Match” compares every line with the order and the goods receipts.',
  'bo.ap.inv.match.ordered': 'Ordered (PO)',
  'bo.ap.inv.match.received': 'Received',
  'bo.ap.inv.match.invoiced': 'Invoiced',
  'bo.ap.inv.match.variance': 'Value variance',
  'bo.ap.inv.match.result': 'Result',
  'bo.ap.inv.match.ok': 'Within tolerance',
  'bo.ap.inv.match.freeLine': 'Not on the purchase order',
  'bo.ap.inv.match.notChecked': 'not checked (2-way)',
  'bo.ap.inv.match.cumulative': 'incl. earlier invoices:',
  'bo.ap.inv.match.qtyVar': 'Qty',
  'bo.ap.inv.match.priceVar': 'Price/unit',
  'bo.ap.inv.match.headerRow': 'Header amount',
  'bo.ap.inv.match.headerRowHint': 'Net printed vs the sum of the lines',
  'bo.ap.inv.match.linesNet': 'Lines',
  'bo.ap.inv.match.totalVariance': 'Total value variance of the lines:',
  'bo.ap.inv.match.allOk': 'every line within tolerance',
  'bo.ap.inv.match.exceptions': '{count} with exceptions',

  // ---- Actions
  'bo.ap.inv.edit': 'Edit draft',
  'bo.ap.inv.submit': 'Submit for approval',
  'bo.ap.inv.submitting': 'Submitting…',
  'bo.ap.inv.submitTitle': 'Submit {number} for approval?',
  'bo.ap.inv.submitBody':
    'The invoice of {total} goes to the approval steps of the policy and can no longer be edited.',
  'bo.ap.inv.submitMatchWarning':
    'It is matched against the order first; an exception adds a “Match exception” step.',
  'bo.ap.inv.submitted': 'Invoice {number} submitted for approval.',
  'bo.ap.inv.approvedNow': 'Invoice {number} approved (no approval steps apply).',
  'bo.ap.inv.post': 'Post',
  'bo.ap.inv.posting': 'Posting…',
  'bo.ap.inv.postTitle': 'Post {number}?',
  'bo.ap.inv.postBody':
    'Posting records a payable of {total} to {supplier}. This cannot be edited afterwards.',
  'bo.ap.inv.postBudgetNote':
    'The net amount becomes an actual on the budget line and releases the order’s commitment.',
  'bo.ap.inv.posted': 'Invoice {number} posted.',
  'bo.ap.inv.dispute': 'Dispute',
  'bo.ap.inv.disputing': 'Saving…',
  'bo.ap.inv.disputeTitle': 'Dispute {number}?',
  'bo.ap.inv.disputeBody':
    'An open approval is cancelled. The invoice waits until it is settled with the supplier.',
  'bo.ap.inv.disputed': 'Invoice {number} disputed.',
  'bo.ap.inv.reopen': 'Reopen as draft',
  'bo.ap.inv.reopening': 'Reopening…',
  'bo.ap.inv.reopenTitle': 'Reopen {number}?',
  'bo.ap.inv.reopenBody': 'It becomes a draft again so it can be corrected, matched and submitted.',
  'bo.ap.inv.reopened': 'Invoice {number} reopened as a draft.',
  'bo.ap.inv.void': 'Void',
  'bo.ap.inv.voiding': 'Voiding…',
  'bo.ap.inv.voidTitle': 'Void {number}?',
  'bo.ap.inv.voidBody': 'A voided invoice stays in the records but is never payable.',
  'bo.ap.inv.voidPostedWarning': 'It is posted: the payable and the budget actual are reversed.',
  'bo.ap.inv.voidReviewWarning': 'The open approval request is cancelled.',
  'bo.ap.inv.voided': 'Invoice {number} voided.',
  'bo.ap.inv.reason': 'Reason',

  // ---- Payments on the invoice
  'bo.ap.inv.paymentsTitle': 'Payments',
  'bo.ap.inv.paymentsDesc':
    'Payments allocated to this invoice. Only verified payments reduce what is outstanding.',
  'bo.ap.inv.paymentsNotPosted': 'Payments can be allocated once the invoice is posted.',
  'bo.ap.inv.paymentsEmpty': 'No payments allocated yet. They are recorded under Payments.',
  'bo.ap.inv.pay.document': 'Payment',
  'bo.ap.inv.pay.date': 'Date',
  'bo.ap.inv.pay.reference': 'Reference',
  'bo.ap.inv.pay.applied': 'Allocated here',
  'bo.ap.inv.pay.status': 'Status',
  'bo.ap.inv.payStatus.pending_verification': 'Awaiting verification',
  'bo.ap.inv.payStatus.verified': 'Verified',
  'bo.ap.inv.payStatus.rejected': 'Rejected',

  // ---- Credit notes
  'bo.ap.credit.title': 'Credit notes',
  'bo.ap.credit.subtitle':
    'Supplier credit notes in {branch}. Entered by AP staff, posted by another person.',
  'bo.ap.credit.readOnly': 'Read only: entering credit notes needs the AP staff role.',
  'bo.ap.credit.readOnlyApprover':
    'You post credit notes entered by AP staff; entering them needs the AP staff role.',
  'bo.ap.credit.noPermission': 'Your role cannot see credit notes in this branch.',
  'bo.ap.credit.listTitle': 'Credit notes',
  'bo.ap.credit.listDesc': 'Newest first. A posted credit note reduces the invoice’s outstanding.',
  'bo.ap.credit.statusFilter': 'Filter by status',
  'bo.ap.credit.status.draft': 'Draft',
  'bo.ap.credit.status.posted': 'Posted',
  'bo.ap.credit.status.voided': 'Voided',
  'bo.ap.credit.empty': 'No credit notes yet. Enter one with “New credit note”.',
  'bo.ap.credit.emptyReadOnly': 'No credit notes yet. They appear once AP staff enter them.',
  'bo.ap.credit.emptyFiltered': 'No credit notes with this status.',
  'bo.ap.credit.emptyInvoice': 'No credit notes for this invoice.',
  'bo.ap.credit.emptyNotPosted': 'Credit notes can be entered once the invoice is posted.',
  'bo.ap.credit.forInvoiceTitle': 'Credit notes',
  'bo.ap.credit.forInvoiceDesc': 'Credits from the supplier against this invoice.',
  'bo.ap.credit.col.number': 'Credit note',
  'bo.ap.credit.col.amount': 'Amount',
  'bo.ap.credit.col.reason': 'Reason',
  'bo.ap.credit.col.status': 'Status',
  'bo.ap.credit.col.actions': 'Actions',
  'bo.ap.credit.enteredBy': 'Entered by {name}',
  'bo.ap.credit.postedBy': 'Posted by {name} · {time}',
  'bo.ap.credit.create': 'New credit note',
  'bo.ap.credit.createTitle': 'New credit note',
  'bo.ap.credit.createDesc': 'Against one posted invoice, at most what is still open on it.',
  'bo.ap.credit.forInvoice': 'Invoice',
  'bo.ap.credit.invoice': 'Invoice',
  'bo.ap.credit.invoiceHint': 'Posted invoices of this supplier with an open balance.',
  'bo.ap.credit.pickInvoice': 'Choose an invoice',
  'bo.ap.credit.noOpenInvoices': 'No open invoices for this supplier',
  'bo.ap.credit.invoiceOption': '{number} · no. {supplierNumber} — {open} open',
  'bo.ap.credit.openAmount': 'Still open on the invoice:',
  'bo.ap.credit.number': 'Credit note number',
  'bo.ap.credit.numberHint': 'As printed on the supplier’s credit note.',
  'bo.ap.credit.amount': 'Amount ({currency})',
  'bo.ap.credit.reason': 'Reason',
  'bo.ap.credit.reasonHint': 'For example a price correction or goods returned.',
  'bo.ap.credit.save': 'Save credit note',
  'bo.ap.credit.sodNote':
    'Separation of duties: the person who entered a credit note cannot post it.',
  'bo.ap.credit.ownNote': 'You entered it — another person posts it.',
  'bo.ap.credit.err.invoice': 'Choose the invoice.',
  'bo.ap.credit.err.number': 'Enter the credit note number (at most 60 characters).',
  'bo.ap.credit.err.amount': 'Enter an amount above 0 in whole rupiah.',
  'bo.ap.credit.err.over': 'The amount is more than what is still open on the invoice.',
  'bo.ap.credit.err.reason': 'Write a reason of 3 to 300 characters.',
  'bo.ap.credit.dupNumber': 'This credit note number is already recorded.',
  'bo.ap.credit.overOpen': 'More than the {open} still open on the invoice.',
  'bo.ap.credit.overOpenGeneric': 'More than what is still open on the invoice.',
  'bo.ap.credit.selfPost':
    'You entered this credit note; another person with AP approval must post it.',
  'bo.ap.credit.created': 'Credit note {number} saved as a draft.',
  'bo.ap.credit.post': 'Post',
  'bo.ap.credit.posting': 'Posting…',
  'bo.ap.credit.postTitle': 'Post credit note {number}?',
  'bo.ap.credit.postBody': '{amount} is credited to invoice {invoice} and reduces its outstanding.',
  'bo.ap.credit.posted': 'Credit note {number} posted.',
  'bo.ap.credit.void': 'Void',
  'bo.ap.credit.voiding': 'Voiding…',
  'bo.ap.credit.voidTitle': 'Void credit note {number}?',
  'bo.ap.credit.voidBody': 'The draft is voided and its amount is released from the invoice.',
  'bo.ap.credit.voided': 'Credit note {number} voided.',
} as const;
